import { useLayoutEffect, useRef } from 'react';
import { gsap, prefersReducedMotion } from '../lib/motion';
import AutoVideo from '../lib/AutoVideo';

export default function Hero() {
  const wrapRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    if (prefersReducedMotion()) return;

    const ctx = gsap.context(() => {
      // Gentle content lift as the page scrolls on — no pinning, no scroll-jack
      gsap.to('.hero-content', {
        yPercent: -18,
        opacity: 0,
        ease: 'none',
        scrollTrigger: { trigger: wrap, start: 'top top', end: 'bottom top', scrub: true },
      });
    }, wrap);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={wrapRef} id="top" className="relative h-screen overflow-hidden vignette">
      <AutoVideo
        src="/videos/hero-aerial.mp4"
        poster="/images/aerial.jpg"
        className="absolute inset-0 h-full w-full"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink-950/70 via-ink-950/25 to-ink-950" />
      {/* center scrim so the title reads over bright footage */}
      <div
        className="absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 45% at 50% 46%, rgba(10,10,9,0.5), transparent 70%)' }}
      />

      <div className="hero-content relative z-10 flex h-full flex-col items-center justify-center px-6 text-center">
        <p className="eyebrow mb-8">Multifamily &amp; Commercial Contractor · San Antonio &amp; Austin</p>
        <h1
          className="font-display text-5xl font-semibold uppercase leading-[1.15] text-steel-100 sm:text-6xl md:text-7xl lg:text-8xl"
          style={{ letterSpacing: '0.18em', marginRight: '-0.18em', textShadow: '0 2px 24px rgba(10,10,9,0.75)' }}
        >
          Revive
          <span className="mx-3 hidden h-px w-8 -translate-y-3 bg-gold-400/70 align-middle sm:inline-block sm:w-14 md:mx-5" />
          <br className="sm:hidden" />
          Builders
        </h1>
        <p
          className="mt-8 max-w-md text-sm font-light tracking-[0.08em] text-steel-200 md:text-base"
          style={{ textShadow: '0 1px 12px rgba(10,10,9,0.8)' }}
        >
          Quality construction. Without compromise.
        </p>
        <a
          href="#contact"
          className="mt-12 inline-flex items-center gap-3 border border-gold-500/40 bg-ink-950/30 px-10 py-4 text-[11px] font-medium uppercase tracking-[0.3em] text-gold-300 backdrop-blur-md transition-all duration-500 hover:border-gold-400 hover:bg-gold-500 hover:text-ink-950"
        >
          Request a Bid
        </a>
      </div>

      <div className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-center">
        <div className="mx-auto h-12 w-px bg-gradient-to-b from-transparent via-gold-400/60 to-transparent" />
        <p className="mt-3 text-[10px] uppercase tracking-widest2 text-steel-400">Scroll</p>
      </div>
    </section>
  );
}
